import { Injectable, inject } from '@angular/core';

import { CartModel, Fai, Option, Plan, Product, ProductFactory, QuoteModel } from '@bytel/bytel-sales';
import {
    CartCheckoutService,
    CheckoutCartModel,
    ConfigurationService,
    LINKED_API_PRODUCT_TYPE,
    OptionsRepository,
    ProductRepository,
} from '@bytel/checkout';

import { GroupedOptions, LinkedProduct } from '@interfaces/checkout.interface';

import { Observable, Subject, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { OPTION_LOCKED_ID } from '../common/constants';
import { FaiService } from './fai.services';

@Injectable({
    providedIn: 'root',
})
export class OptionsService {
    public optionsRefreshObs: Observable<Option[]>;

    protected options: { [key: string]: Option[] } = {};
    private _optionsRefresh: Subject<Option[]> = new Subject();

    private productRepositoryService: ProductRepository = inject(ProductRepository);
    private optionRepository: OptionsRepository = inject(OptionsRepository);
    private cartService: CartCheckoutService = inject(CartCheckoutService);
    private configService: ConfigurationService = inject(ConfigurationService);
    private faiService: FaiService = inject(FaiService);

    public constructor() {
        this.optionsRefreshObs = this._optionsRefresh.asObservable();
    }

    public static HasBlockedOption(cartModel: CartModel, quoteIndex: number = cartModel.currentQuoteIndex): boolean {
        const quote: QuoteModel = cartModel.getQuote(quoteIndex);
        return !!quote.getProductByGencode(OPTION_LOCKED_ID);
    }

    public initializeOptions(cart: CheckoutCartModel): Observable<boolean> {
        const obs: Observable<Option[]>[] = [];

        cart.quotes.forEach((quote) => {
            obs.push(this.getCartOptionsByQuote(cart, quote));
        });

        return forkJoin(obs).pipe(
            map((result) => {
                const optionsCount = result.reduce((acc, options) => acc + options.length, 0);
                return optionsCount > 1;
            }),
        );
    }

    public getCartOptionsByQuote(cart: CheckoutCartModel, quote: QuoteModel): Observable<Option[]> {
        let obs: Observable<Option[]>;
        if (quote.isAcquisitionFix()) {
            const fai: Fai = quote.getProductByType<Fai>('Fai');
            if (!fai) {
                obs = of([]);
            } else {
                obs = this._getFaiOptionsByPlan(cart, fai, quote);
            }
        } else {
            const plan: Plan = quote.getProductByType<Plan>('Plan');
            obs = this._getMobileOptionByPlan(plan, cart, quote);
        }

        return obs;
    }

    public getOptionsByQuote(quote: QuoteModel): Option[] {
        const plan: Plan = quote.getProductByType(Plan);
        return plan ? this.getOptionsByPlan(plan) : [];
    }

    public getOptionsByPlan(plan: Plan): Option[] {
        return this.options[plan.gencode] || [];
    }

    public getSortedOptionsByQuote(quote: QuoteModel): Observable<Option[]> {
        const options = this.getOptionsByQuote(quote);
        const plan: Plan = quote.getProductByType(Plan);
        if (plan) {
            return this.productRepositoryService
                .getLinkedProductAttributes(plan.gencode, LINKED_API_PRODUCT_TYPE.UPSELL)
                .pipe(
                    map((linkedProducts) => {
                        this._setFirstPageOption(options, linkedProducts);
                        return this._sortProductsByList(options, linkedProducts);
                    }),
                );
        }
        return of([]);
    }

    /**
     * Group a list of options by type
     * @param options List to group
     * @param excludeParental boolean to exclude parental control options from grouped list - false by default
     * @returns List of grouped options @type Option[]
     */
    public groupOptionsByType(options: Option[], excludeParental: boolean = false): GroupedOptions {
        const groupedOptions: GroupedOptions = {
            firstPageOption: [],
            bulletOption: [],
            commonOptions: [],
        };

        if (excludeParental) {
            options = this.excludeParentalOptions(options);
        }

        const firstPageOption = options.filter((option) => option.data.firstPage || option.data.first_page);
        groupedOptions.bulletOption = firstPageOption ?? null;
        options
            .filter((option) => !option.data.firstPage && !option.data.first_page)
            .forEach((option: Option) => {
                if (
                    option.data.bullet_description &&
                    !groupedOptions.bulletOption.find((opt) => opt.gencode === option.gencode)
                ) {
                    groupedOptions.bulletOption.push(option);
                } else {
                    groupedOptions.commonOptions.push(option);
                }
            });
        return groupedOptions;
    }

    public excludeParentalOptions(options: Option[]): Option[] {
        return this._cleanParentalOptions(options, false);
    }

    public filterParentalOptions(options: Option[]): Option[] {
        return this._cleanParentalOptions(options).sort((optionA, optionB) => {
            const isOptionAPremium: boolean = this.getParentalControlPremiumGencodes().includes(optionA.gencode);
            const isOptionBPremium: boolean = this.getParentalControlPremiumGencodes().includes(optionB.gencode);
            if (isOptionAPremium && isOptionBPremium) {
                return 0;
            } else if (isOptionBPremium) {
                return 1;
            }
            return -1;
        });
    }

    public getParentalControlFreeGencodes(): string[] {
        return this.configService.getData()?.global.checkout.parental_control_free.split(',');
    }

    public getParentalControlPremiumGencodes(): string[] {
        return this.configService.getData()?.global.checkout.parental_control_premium.split(',');
    }

    public getParentalControlGencodes(): string[] {
        return this.getParentalControlFreeGencodes().concat(this.getParentalControlPremiumGencodes());
    }

    private _cleanParentalOptions(options: Option[], withParentalOptions: boolean = true): Option[] {
        return options.filter(
            (option) => withParentalOptions === this.getParentalControlGencodes().includes(option.gencode),
        );
    }

    private _getFaiOptionsByPlan(cartModel: CheckoutCartModel, fai: Fai, quote: QuoteModel): Observable<Option[]> {
        if (!fai || !fai?.upSells.length) {
            return of([]);
        }

        let initGencodeObs: Observable<Option[]>;
        let initObs: Observable<Product[]>;

        if (!this.options[fai.gencode]) {
            // Get all available options for this fai plan
            initGencodeObs = quote.isRenewal()
                ? this.optionRepository.getRenewOptions(quote, fai)
                : this.optionRepository.getAcquisitionOptions(
                      cartModel,
                      fai,
                      null,
                      this.faiService.eligibility?.cartId || null,
                  );

            initObs = initGencodeObs.pipe(
                map((options) => options.map((opt) => opt.gencode)),
                map((optionsGencodes) => fai.upSells.filter((gencode) => optionsGencodes.includes(gencode))),
                mergeMap((upsellsGencodes) => this.productRepositoryService.getProductsByGencodes(upsellsGencodes)),
                tap((upsells) => {
                    // save defaults options
                    this.options[fai.gencode] = upsells
                        .map((option) => option as Option)
                        .filter((upsell) => ProductFactory.Is(upsell, Option));
                }),
            );
        } else {
            initObs = of(this.options[fai.gencode]);
        }

        // Get options compatibles with current options selected
        const selectedOptions: string[] = this._getSelectedOptions(quote);
        const resource: Observable<unknown> = quote.isRenewal()
            ? this.optionRepository.getRenewOptions(quote, fai, selectedOptions)
            : this.optionRepository.getAcquisitionOptions(
                  cartModel,
                  fai,
                  selectedOptions,
                  this.faiService.eligibility?.cartId || null,
              );

        return initObs.pipe(
            mergeMap(() => resource),
            mergeMap((compatibles: Option[]) => this._setIncompatiblesOptions(compatibles, fai, quote)),
            map(() => this.getOptionsByPlan(fai)),
            catchError(() => of([])),
        );
    }

    private _getMobileOptionByPlan(plan: Plan, cartModel: CartModel, quote: QuoteModel): Observable<Option[]> {
        if (!plan || !plan?.upSells.length) {
            return of([]);
        }

        let initGencodeObs: Observable<Option[]>;
        let initObs: Observable<Product[]>;

        if (!this.options[plan.gencode]) {
            initGencodeObs = quote.isRenewal()
                ? this.optionRepository.getRenewOptions(quote, plan)
                : this.optionRepository.getAcquisitionOptions(cartModel, plan);

            initObs = initGencodeObs.pipe(
                map((options) => options.map((opt) => opt.gencode)),
                map((optionsGencodes) => plan.upSells.filter((gencode) => optionsGencodes.includes(gencode))),
                mergeMap((upsellsGencodes) => this.productRepositoryService.getProductsByGencodes(upsellsGencodes)),
                tap((upsells) => {
                    // save defaults options
                    this.options[plan.gencode] = upsells
                        .map((option) => option as Option)
                        .filter((upsell) => ProductFactory.Is(upsell, Option));
                }),
            );
        } else {
            initObs = of(this.options[plan.gencode]);
        }

        const selectedOptions: string[] = this._getSelectedOptions(quote);
        const resource: Observable<unknown> = quote.isRenewal()
            ? this.optionRepository.getRenewOptions(quote, plan, selectedOptions)
            : this.optionRepository.getAcquisitionOptions(cartModel, plan, selectedOptions);

        return initObs.pipe(
            mergeMap(() => resource),
            mergeMap((compatibles: Option[]) => this._setIncompatiblesOptions(compatibles, plan, quote)),
            map(() => this.getOptionsByPlan(plan)),
            catchError(() => of([])),
        );
    }

    private _getSelectedOptions(quote: QuoteModel): string[] {
        const options = quote.getProductsByType<Option>('Option');
        return options.map((option) => option.gencode);
    }

    private _setFaiCompatibility(cartModel: CheckoutCartModel, plan: Plan, quote: QuoteModel): Observable<Option[]> {
        const removeObs: Observable<Product>[] = [];
        const selectedOptions: Option[] = quote.getProductsByType<Option>('Option');
        const incompatibleOptions: Set<string> = new Set<string>();
        for (const option of selectedOptions) {
            if (option.data.incompatible_with) {
                for (const item of option.data.incompatible_with) {
                    incompatibleOptions.add(item);
                }
            }
        }

        const options: Option[] = this.getOptionsByPlan(plan);

        for (const option of options) {
            option.data.enabled = !incompatibleOptions.has(option.gencode);
            if (!option.data.enabled) {
                const selectedOption: Option = selectedOptions.find((opt: Option) => opt.gencode === option.gencode);
                if (selectedOption) {
                    removeObs.push(
                        this.cartService.removeProduct(cartModel, selectedOption, cartModel.currentQuoteIndex, false),
                    );
                }
            }
        }

        if (removeObs.length > 0) {
            return forkJoin(removeObs).pipe(
                mergeMap(() => from(this.cartService.refresh(cartModel))),
                map(() => options),
            );
        } else {
            return of(options);
        }
    }

    private _setIncompatiblesOptions(compatibles: Option[], plan: Plan, quote: QuoteModel): Observable<Option[]> {
        const selectedOptions: string[] = this._getSelectedOptions(quote);
        const options: Option[] = this.getOptionsByPlan(plan);
        options.forEach((option) => {
            option.data.enabled =
                compatibles.some((opt) => opt.gencode === option.gencode) ||
                selectedOptions.some((gencode) => gencode === option.gencode);
        });

        return of(options);
    }

    private _sortProductsByList(products: Option[], upsells: LinkedProduct[]): Option[] {
        return products.sort((a: Option, b: Option) =>
            Math.sign(this._getPositionByGencode(a.gencode, upsells) - this._getPositionByGencode(b.gencode, upsells)),
        );
    }

    private _getPositionByGencode(gencode: string, upsells: LinkedProduct[]): number {
        const upsell: LinkedProduct = upsells.find((up) => up.gencode === gencode);
        return upsell ? upsell.position : 0;
    }

    private _setFirstPageOption(options: Option[], linkedProducts: LinkedProduct[]): void {
        const firstPageLinked = linkedProducts.filter((link) => link.firstPage);
        if (firstPageLinked) {
            firstPageLinked.forEach((product) => {
                const firstPageOption = options.find((upsell) => upsell.gencode === product.gencode);
                if (firstPageOption) {
                    firstPageOption.data.firstPage = true;
                }
            });
        }
    }
}
